// src/utils/crossAppAuth.js
import axios from 'axios';

const HOMEPAGE_API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000'
  : 'https://satoshi-homepage-59650ad0a3ce.herokuapp.com';

const TOKEN_KEY = 'satoshi_cross_app_token';
const USER_KEY = 'satoshi_user_data';

// Get stored token
export const getCrossAppToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

// Store token
export const storeCrossAppToken = (token) => {
  localStorage.setItem(TOKEN_KEY, token);
};

// Read a cookie by name (used for the shared .satoshiadvance.com session)
export const readSharedCookie = (name) => {
  const match = document.cookie.split(';').map((c) => c.trim()).find((c) => c.startsWith(name + '='));
  if (!match) return null;
  try {
    return decodeURIComponent(match.slice(name.length + 1));
  } catch (e) {
    return match.slice(name.length + 1);
  }
};

// Clear only this app's local copy of the session. Used when another
// Satoshi Advance app already logged the user out (the shared cookies are
// gone) — clearing cookies again could wipe a newer user's session.
export const clearLocalSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem('user');
  localStorage.removeItem('userEmail');
  sessionStorage.removeItem('userEmail');
  sessionStorage.removeItem('user');
};

// Remove token (logout): clears local storage AND the shared
// .satoshiadvance.com cookies, so every mini game signs out too.
export const removeCrossAppToken = () => {
  clearLocalSession();
  document.cookie = 'userEmail=; domain=.satoshiadvance.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
  document.cookie = 'satoshiToken=; domain=.satoshiadvance.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
  document.cookie = 'userEmail=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
};

// Tell the backend to invalidate the account's cross-app tokens so other
// apps' stored sessions stop validating (best-effort, fire and forget).
export const logoutEverywhere = (email) => {
  if (!email) return;
  axios.post(`${HOMEPAGE_API}/api/users/logout`, { email }).catch((error) => {
    console.log('Global logout call failed (cookies are cleared anyway):', error.message);
  });
};

// Store user data
export const storeUserData = (userData) => {
  localStorage.setItem(USER_KEY, JSON.stringify(userData));
};

// Get user data
export const getUserData = () => {
  const data = localStorage.getItem(USER_KEY);
  return data ? JSON.parse(data) : null;
};

// Validate token with homepage API
export const validateCrossAppToken = async (token) => {
  try {
    const response = await axios.post(`${HOMEPAGE_API}/api/cross-app/validate-token`, {
      token
    });

    if (response.data.success) {
      // Store user data
      storeUserData({
        email: response.data.email,
        isVerified: response.data.isVerified,
        roles: response.data.roles
      });
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('Token validation failed:', error);
    removeCrossAppToken();
    return null;
  }
};

// Generate cross-app token after login
export const generateCrossAppToken = async (email, role = null) => {
  try {
    const response = await axios.post(`${HOMEPAGE_API}/api/cross-app/generate-token`, {
      email,
      role
    });

    if (response.data.success) {
      storeCrossAppToken(response.data.token);
      storeUserData({
        email: response.data.email,
        roles: response.data.roles
      });
      return response.data.token;
    }
    return null;
  } catch (error) {
    console.error('Token generation failed:', error);
    return null;
  }
};

// Check if user is logged in
export const isLoggedIn = () => {
  return !!getCrossAppToken();
};

// Auto-login on app load
export const autoLogin = async () => {
  const token = getCrossAppToken();

  if (!token) {
    return null;
  }

  const userData = await validateCrossAppToken(token);
  return userData;
};
